// SSE client for the Intel realtime feed.
//
// Contract (matches doc/intel-realtime-architecture.md):
//   * Connects to /v1/realtime/intel/sse with the access JWT in a query param
//     (EventSource cannot set Authorization headers).
//   * Reconnects on close with exponential backoff: 1s → 2s → 4s → 8s → 30s.
//   * On every reconnect, re-sends `Last-Event-ID` so the server can replay
//     the gap from NATS. EventSource handles this header automatically when
//     we set the `id:` field on each parsed frame, but we also persist the
//     highest seen id to IDB so a fresh page load resumes from where we
//     stopped, not from `now`.
//   * Parses `event: signal` and `event: signal_batch`. Anything else is
//     ignored. Malformed bodies are dropped (Zod parse fail) and counted.
//   * Subscribers receive parsed frames via a typed callback. No DOM globals
//     leak through — the subscriber receives `IntelSignal[]` only.

import type { ParsedFrame } from './types.js';
import { SignalBatchEvent, SignalEvent } from './types.js';

export type ConnectionStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface IntelSseOptions {
  /** Full SSE URL, e.g. `https://api.eidollon.app/v1/realtime/intel/sse`. */
  url: string;
  /** Access JWT (15-min). Re-mint and reconnect on rotation. */
  token: string;
  /** Resume id from IDB (highest event id previously seen, if any). */
  lastEventId?: string | null;
  /** Optional EventSource ctor override — for tests. */
  eventSourceCtor?: typeof EventSource;
}

export interface IntelSseClient {
  start(): void;
  stop(): void;
  on(handler: (frame: ParsedFrame) => void): () => void;
  status(): ConnectionStatus;
  /** Dropped frames since start (parse fail or unknown event type). */
  droppedFrames(): number;
  /** Highest event id observed across the lifetime of this client. */
  lastEventId(): string | null;
}

const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 30_000] as const;

export function createIntelSseClient(opts: IntelSseOptions): IntelSseClient {
  const ctorOrNull =
    opts.eventSourceCtor ?? (typeof EventSource !== 'undefined' ? EventSource : null);
  if (!ctorOrNull) throw new Error('EventSource not available in this environment');
  const Ctor: typeof EventSource = ctorOrNull;

  let es: EventSource | null = null;
  let attempt = 0;
  let stopped = false;
  let status: ConnectionStatus = 'idle';
  let lastId: string | null = opts.lastEventId ?? null;
  let dropped = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  const handlers = new Set<(frame: ParsedFrame) => void>();

  function buildUrl(): string {
    const u = new URL(opts.url);
    u.searchParams.set('access_token', opts.token);
    // EventSource will send Last-Event-ID automatically once the server has
    // emitted at least one `id:` line during the lifetime of *this*
    // connection. On a fresh page load we have no in-flight EventSource yet,
    // so we pass our hydrated id as a query param too. Server reads either.
    if (lastId) u.searchParams.set('last_event_id', lastId);
    return u.toString();
  }

  function emit(frame: ParsedFrame) {
    if (frame.eventId) lastId = frame.eventId;
    for (const h of handlers) h(frame);
  }

  function tryParseSignal(raw: MessageEvent) {
    try {
      const parsed = SignalEvent.parse(JSON.parse(raw.data));
      emit({ kind: 'signal', signal: parsed, eventId: raw.lastEventId || null });
    } catch {
      dropped += 1;
    }
  }

  function tryParseBatch(raw: MessageEvent) {
    try {
      const parsed = SignalBatchEvent.parse(JSON.parse(raw.data));
      emit({ kind: 'signal_batch', batch: parsed, eventId: raw.lastEventId || null });
    } catch {
      dropped += 1;
    }
  }

  function open() {
    if (stopped) return;
    status = es ? 'reconnecting' : 'connecting';
    es?.close();
    es = new Ctor(buildUrl());

    es.addEventListener('open', () => {
      status = 'open';
      attempt = 0;
    });
    es.addEventListener('signal', tryParseSignal as (e: Event) => void);
    es.addEventListener('signal_batch', tryParseBatch as (e: Event) => void);
    es.addEventListener('error', () => {
      // EventSource auto-retries by default but we control backoff explicitly
      // so reconnects share the JWT + Last-Event-ID handling above.
      es?.close();
      if (stopped) return;
      const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)] ?? 30_000;
      attempt += 1;
      status = 'reconnecting';
      reconnectTimer = setTimeout(open, delay);
    });
  }

  return {
    start() {
      stopped = false;
      attempt = 0;
      open();
    },
    stop() {
      stopped = true;
      status = 'closed';
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = null;
      es?.close();
      es = null;
    },
    on(handler) {
      handlers.add(handler);
      return () => handlers.delete(handler);
    },
    status: () => status,
    droppedFrames: () => dropped,
    lastEventId: () => lastId,
  };
}
